/** Shared Detail host theme, including width-responsive property label tracks. */
import { DesignSystem } from '@workbuddy/ui/v2/design-system';

const tokens = DesignSystem.tokens;

export const detailHostStyleId = 'wb-v2-surface-detail-host-theme';

export const detailHostBridgeStyles = {
  'wb-surface-detail-host': {
    '--local-detail-canvas-bg': tokens.color.background.primary,
    // Figma 3141-26183 body rhythm, all container-owned (no section margins):
    // gap between sections, 4px inside a schema `group`. `.spacer()` is a
    // 1px divider; gap on both sides is the breathing room.
    '--local-detail-gap': tokens.spacing.xl,
    '--local-detail-list-gap': tokens.spacing.xs,
    '--local-detail-spacer-size': tokens.borderWidth.sm,
    '--local-detail-gap-spacer-size': tokens.spacing.md,
    // Compact (rail / board panel / dialog under 720px): 16px between sections.
    // Divider stays 1px. Gap spacers add nothing. 20px padding.
    '--local-detail-compact-gap': tokens.spacing.xl,
    '--local-detail-compact-spacer-size': tokens.borderWidth.sm,
    '--local-detail-compact-gap-spacer-size': '0px',
    '--local-detail-compact-padding-y': tokens.spacing.twoXl,
    // Compact dialogs/rails used the same 20px on the last row as the top; scroll ended on the edge.
    '--local-detail-compact-padding-bottom': tokens.spacing.fourXl,
    '--local-detail-relation-height': '320px',
    '--local-detail-property-label-width': '184px',
    '--local-detail-wide-property-label-width': tokens.spacing.elevenXl,
    // Compact (rail / board panel / dialog): 184px would leave a 440px rail a sliver of value column.
    // A cap, not a width: the shared label column only grows as far as its labels need. 128px wrapped "Requires approval (?)".
    '--local-detail-compact-property-label-width': tokens.spacing.elevenXl,
    // Label→value gap of the shared label track. The section container owns it (property-list bodies
    // subgrid with `column-gap: normal`), so it carries wb-section's property column gap value.
    '--local-detail-property-column-gap': tokens.spacing.xl,
    '--local-detail-padding-top': tokens.spacing.fiveXl,
    '--local-detail-padding-bottom': tokens.spacing.sevenXl,
    '--local-detail-padding-x': tokens.spacing.twoXl,
    '--local-detail-content-max-width': '800px',
    '--local-detail-title-size': tokens.font.size.textMdBase,
    '--local-detail-title-weight': tokens.font.weight.semibold,
    '--local-detail-title-line': tokens.lineHeight.textMd,
    '--local-detail-title-color': tokens.color.text.primary,
    // Kind ("Visit approval ·") reads as a quiet qualifier of the bold identifier.
    // Readable floor for the header title row, so a wide action bar is squeezed
    // (and folds into More) before the title starts losing characters.
    '--local-detail-title-min-width': '160px',
    // Quick view / rail: a tighter floor, so the action bar folds into More
    // rather than the title losing its readable head.
    '--local-detail-compact-title-min-width': '96px',
    // Chrome slot buttons (back / pin / close) share one hit target size.
    '--local-detail-chrome-button-size': '28px',
    '--local-detail-chrome-icon-size': tokens.font.size.textSm,
    '--local-detail-chrome-icon-line-height': '1',
    '--local-detail-header-row-gap': tokens.spacing.xs,
    '--local-detail-compact-header-row-gap': tokens.spacing.md,
    '--local-detail-tab-text': tokens.color.text.tertiary,
    '--local-detail-tab-text-active': tokens.color.text.brandSecondary,
    '--local-detail-tab-underline': tokens.color.text.brandSecondary,
    '--local-detail-panel-header-padding-right': tokens.spacing.md,
    '--local-detail-save-gap': tokens.spacing.sm,
    '--local-detail-step-gap': tokens.spacing.xxs,
    '--local-detail-step-color': tokens.color.text.tertiary,
    '--local-detail-step-font-size': tokens.font.size.textXs,
    '--local-detail-step-line-height': tokens.lineHeight.textXs,
    '--local-detail-rail-guest-head-height': '40px',
    '--local-detail-error-bg': tokens.color.background.errorPrimary,
    '--local-detail-error-border': tokens.color.border.error,
    '--local-detail-error-text': tokens.color.text.errorPrimary,
    '--local-detail-error-radius': tokens.radius.md,
    '--local-detail-banner-padding-y': tokens.spacing.md,
    '--local-detail-banner-padding-x': tokens.spacing.lg,
    '--local-detail-rail-width': '440px',
    '--local-detail-rail-dock-width': '48px',
    '--local-detail-rail-border': tokens.color.border.secondary,
    // Seam between body and rail inside a reading frame (`chrome.frame`), where the rail has no border.
    '--local-detail-frame-gap': tokens.spacing.fiveXl,
    // Line box of a framed body's first line (display-md heading by convention), centred on the rail head row.
    '--local-detail-frame-lead-line': tokens.lineHeight.displayMd,
    '--local-detail-rail-section-pad': tokens.spacing.xl,
    '--local-detail-rail-title-gap': tokens.spacing.sm,
    '--local-detail-rail-overlay-shadow': tokens.shadow.lg,
    '--local-detail-scrollbar-size': '6px',
    '--local-detail-scrollbar-thumb': tokens.color.border.primary,
    '--local-detail-scrollbar-thumb-hover': tokens.color.border.secondaryAlt,
    '--local-detail-scrollbar-track': 'transparent',
    '--local-detail-scrollbar-radius': tokens.radius.full,
  },
};

export function mountDetailHostTheme(document: Document): void {

  DesignSystem.Mount(document, detailHostStyleId, detailHostBridgeStyles);
}
